import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useAuth } from "./AuthContext";
import { starterCategories } from "./data/categories";
import { requireSupabase } from "./lib/supabase";
import type { RecipeCategory } from "./types";

interface CategoryContextValue {
  categories: RecipeCategory[];
  loading: boolean;
  error: string;
  addCategory: (name: string) => Promise<void>;
  refreshCategories: () => Promise<void>;
}

interface CategoryRow {
  id: string;
  name: string;
  cover_key: string | null;
  is_fixed: boolean;
  created_by: string | null;
  created_at: string;
}

const CategoryContext = createContext<CategoryContextValue | null>(null);

function fromRow(row: CategoryRow): RecipeCategory {
  return {
    id: row.id,
    name: row.name,
    coverKey: row.cover_key ?? undefined,
    isFixed: row.is_fixed,
    createdBy: row.created_by ?? undefined,
    createdAt: row.created_at,
  };
}

export function CategoryProvider({ children }: { children: ReactNode }) {
  const { user, isDemo } = useAuth();
  const [categories, setCategories] = useState<RecipeCategory[]>(isDemo ? starterCategories : []);
  const [loading, setLoading] = useState(!isDemo);
  const [error, setError] = useState("");

  const refreshCategories = useCallback(async () => {
    if (!user) return;
    if (isDemo) {
      setCategories((current) => current.length ? current : starterCategories);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error: loadError } = await requireSupabase()
      .from("recipe_categories")
      .select("id, name, cover_key, is_fixed, created_by, created_at")
      .order("is_fixed", { ascending: false })
      .order("name", { ascending: true });
    if (loadError) setError(loadError.message);
    else {
      setCategories((data as CategoryRow[]).map(fromRow));
      setError("");
    }
    setLoading(false);
  }, [isDemo, user]);

  useEffect(() => {
    void refreshCategories();
  }, [refreshCategories]);

  const value = useMemo<CategoryContextValue>(() => ({
    categories,
    loading,
    error,
    async addCategory(name) {
      const normalized = name.trim();
      if (normalized.length < 2 || normalized.length > 60) {
        throw new Error("Le nom doit contenir entre 2 et 60 caractères.");
      }
      if (isDemo) {
        setCategories((current) => {
          if (current.some((item) => item.name.toLocaleLowerCase("fr") === normalized.toLocaleLowerCase("fr"))) {
            return current;
          }
          return [
            ...current,
            { id: `demo-${normalized}`, name: normalized, isFixed: false, createdBy: user?.id },
          ].sort((a, b) => a.name.localeCompare(b.name, "fr"));
        });
        return;
      }
      const { error: createError } = await requireSupabase().rpc("create_recipe_category", {
        category_name: normalized,
      });
      if (createError) throw createError;
      await refreshCategories();
    },
    refreshCategories,
  }), [categories, error, isDemo, loading, refreshCategories, user]);

  return <CategoryContext.Provider value={value}>{children}</CategoryContext.Provider>;
}

export function useCategories() {
  const value = useContext(CategoryContext);
  if (!value) throw new Error("useCategories doit être utilisé dans CategoryProvider.");
  return value;
}
